import * as S from './AddressValidationErrorDialog.styles'
import { GrowTransition } from 'components/Transitions/Transitions'
import useScreenSize from '../../hooks/useScreenSize'
import { translateManager as t } from '../../services/TranslateManager'
import { COUNTRIES, getDefaultCountryId } from '../../config/countries-data'
import { type Address } from '../Pricing/types/checkout.types'
import funnyFaceIcon from '../../images/icons/funnyFace.svg'

interface AddressValidationErrorDialogProps {
  open: boolean
  address: Address | null
  validationError: string
  allowUseAsIs: boolean
  onUseAsIs: () => void
  onEditAddress: () => void
}

function getAddressString(address: Address | null): string {
  if (!address) return ''

  const addressComponents = []

  if (address.street) {
    const street = address.address_2
      ? [address.street, address.address_2].join(' ')
      : address.street
    addressComponents.push(street)
  }

  const secondAddressLine = []
  if (address.city) {
    secondAddressLine.push(address.city)
  }
  if (address.state) {
    secondAddressLine.push(address.state)
  }
  if (address.zipCode) {
    secondAddressLine.push(address.zipCode)
  }

  if (secondAddressLine.length > 0) {
    addressComponents.push(secondAddressLine.join(' '))
  }

  const countryId = address.countryId || getDefaultCountryId()
  addressComponents.push(COUNTRIES[countryId]?.name)

  return addressComponents.join('\n')
}

function Content({
  address,
  validationError,
  allowUseAsIs,
  onUseAsIs,
  onEditAddress,
}: Omit<AddressValidationErrorDialogProps, 'open'>) {
  return (
    <S.Container data-testid="address-validation-error-dialog">
      <S.Icon src={funnyFaceIcon} alt="Address error" />
      <S.Title>
        {validationError}.{' '}
        {t.get('general.address_form.validation_errors.mind_another_look')}
      </S.Title>
      {address && <S.Address>{getAddressString(address)}</S.Address>}
      <S.ActionButton
        onClick={onEditAddress}
        data-testid="address-edit-address"
      >
        {t.get('general.address_form.re_enter_address')}
      </S.ActionButton>
      {allowUseAsIs && (
        <S.SecondaryAction onClick={onUseAsIs} data-testid="address-use-as-is">
          {t.get('general.address_form.use_this_address_anyway')}
        </S.SecondaryAction>
      )}
    </S.Container>
  )
}

function AddressValidationErrorDialog({
  open,
  ...contentProps
}: AddressValidationErrorDialogProps) {
  const isMediumScreen = useScreenSize('md')

  if (isMediumScreen) {
    return (
      <S.Dialog
        disableEscapeKeyDown
        open={open}
        TransitionComponent={GrowTransition}
      >
        <Content {...contentProps} />
      </S.Dialog>
    )
  }

  return (
    <S.BottomDrawer
      anchor="bottom"
      open={open}
      onClose={contentProps.onEditAddress}
    >
      <Content {...contentProps} />
    </S.BottomDrawer>
  )
}

export default AddressValidationErrorDialog
